/**
 * Tutor details tab.
 * Metrics, personal info, program, subjects, availability, and hour targets.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  BookOpen,
  Briefcase,
  Calendar,
  Clock,
  Info,
  LogOut,
  Mail,
  MapPin,
  Phone,
  User,
} from 'lucide-react';
import { getSubjects } from '@/services/subjects.service';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { DetailAvailabilityGrid } from '@/components/common/details/DetailAvailabilityGrid';
import type { WeeklyAvailabilitySlot } from '@/types/user.types';
import type { TutorRecord } from '@/types/inperson/tutor.types';
import {
  computeTutorSeniority,
  formatTutorShortDate,
  getTutorCoursePathLabel,
  getTutorTutoringTypeLabel,
  mapTutorProgramChips,
  resolveTutorSubjectLabels,
  type TutorContentDisplay,
} from '@/components/tutors/inperson/tutor-table-utils';
import { rankedContactDetailFields } from '@/utils/user-contact-utils';

/** Props for {@link TutorDetailsTab}. */
interface Props {
  record: TutorRecord;
  display: TutorContentDisplay;
}

/** Tutor details tab. */
export const TutorDetailsTab: React.FC<Props> = ({ record, display }) => {
  const {
    myInfo,
    genderLabel,
    proSituationLabel,
    proSituationCompanyName,
    proSituationEstablishmentName,
    residenceDepartment,
    isDeactivated,
    exitDateFormatted,
    exitReasonDisplay,
    availability,
    targetHoursLabel,
    hoursDoneLabel,
    subjectIds,
    effectiveEngagementStartDate,
    effectiveEngagementEndDate,
    hasProgramSection,
    myEngagement,
  } = display;

  const { data: allSubjects = [] } = useQuery({
    queryKey: ['subjects'],
    queryFn: async () => (await getSubjects()).data,
  });
  /** Subject labels resolved against the catalog (unknown ids dropped). */
  const subjects = resolveTutorSubjectLabels(subjectIds, allSubjects);

  return (
    <>
      <DetailsCard icon={Info} title="Métriques" colorName="color-3" colSpan={2}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {record.registrationManager && (
            <DetailField
              icon={User}
              label="Gestionnaire recruteur"
              value={`${record.registrationManager.firstName} ${record.registrationManager.lastName}`}
            />
          )}
          <DetailField
            icon={Calendar}
            label="Date d'inscription"
            value={formatTutorShortDate(record.createdAt)}
          />
          <DetailField
            icon={Clock}
            label="Ancienneté"
            value={computeTutorSeniority(record.createdAt)}
          />
          {effectiveEngagementStartDate && (
            <DetailField
              icon={Calendar}
              label="Début d'engagement"
              value={formatTutorShortDate(effectiveEngagementStartDate)}
            />
          )}
          {effectiveEngagementEndDate && (
            <DetailField
              icon={Calendar}
              label="Fin d'engagement"
              value={formatTutorShortDate(effectiveEngagementEndDate)}
            />
          )}
        </div>
      </DetailsCard>

      <DetailsCard icon={User} title="Informations personnelles" colorName="color-2" colSpan={2}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {rankedContactDetailFields(
            record.user?.primaryContactKind,
            record.user?.email,
            record.user?.phone,
          ).map((field) => (
            <DetailField
              key={field.label}
              icon={field.iconKind === 'phone' ? Phone : Mail}
              label={field.label}
              value={field.value}
            />
          ))}
          {genderLabel && <DetailField icon={User} label="Genre" value={genderLabel} />}
          {proSituationLabel && (
            <DetailField
              icon={Briefcase}
              label="Situation professionnelle"
              value={proSituationLabel}
            />
          )}
          {proSituationCompanyName && (
            <DetailField
              icon={Briefcase}
              label="Nom de l'entreprise"
              value={proSituationCompanyName}
            />
          )}
          {proSituationEstablishmentName && (
            <DetailField label="Nom de l'établissement" value={proSituationEstablishmentName} />
          )}
          {myInfo.address && <DetailField icon={MapPin} label="Adresse" value={myInfo.address} />}
          {(myInfo.zipCode || myInfo.city) && (
            <DetailField
              label="Ville"
              value={[myInfo.zipCode, myInfo.city].filter(Boolean).join(' ')}
            />
          )}
          {myInfo.country && <DetailField label="Pays" value={myInfo.country} />}
          {residenceDepartment && (
            <DetailField
              icon={MapPin}
              label="Département de résidence"
              value={residenceDepartment}
            />
          )}
          {isDeactivated && exitDateFormatted && (
            <DetailField icon={LogOut} label="Date de désactivation" value={exitDateFormatted} />
          )}
          {isDeactivated && exitReasonDisplay && (
            <DetailField icon={LogOut} label="Motif de sortie" value={exitReasonDisplay} />
          )}
        </div>
      </DetailsCard>

      {hasProgramSection && (
        <DetailsCard icon={BookOpen} title="Programme" colorName="color-3" colSpan={2}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            {myEngagement.tutoringType && (
              <DetailField
                label="Type d'accompagnement"
                value={getTutorTutoringTypeLabel(myEngagement.tutoringType)}
              />
            )}
            {myEngagement.coursePath && (
              <DetailField
                label="Parcours"
                value={getTutorCoursePathLabel(myEngagement.coursePath)}
              />
            )}
            {Array.isArray(myEngagement.programs) && myEngagement.programs.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground detail-field-label">
                  Programme(s)
                </span>
                <DetailChipList
                  items={mapTutorProgramChips(myEngagement.programs)}
                  colorScheme="color-3"
                  variant="outlined"
                />
              </div>
            )}
          </div>
        </DetailsCard>
      )}

      <DetailsCard icon={BookOpen} title="Matières" colorName="color-3" colSpan={1}>
        {subjects.length > 0 ? (
          <DetailChipList
            items={subjects.map((s) => ({ id: s.id, label: s.label }))}
            colorScheme="color-2"
            variant="filled"
          />
        ) : (
          <p className="text-sm text-muted-foreground">Aucune matière renseignée</p>
        )}
      </DetailsCard>

      <DetailsCard icon={Clock} title="Disponibilités" colorName="color-2" colSpan={1}>
        {availability?.weekly && availability.weekly.length > 0 ? (
          <DetailAvailabilityGrid
            slots={availability.weekly as WeeklyAvailabilitySlot[]}
            variant="compact"
          />
        ) : (
          <p className="text-sm text-muted-foreground">Aucune disponibilité renseignée</p>
        )}
      </DetailsCard>

      {targetHoursLabel && (
        <DetailsCard icon={Award} title="Objectifs" colorName="color-3" colSpan={2}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <DetailField label="Heures à réaliser" value={targetHoursLabel} />
            {hoursDoneLabel && <DetailField label="Heures réalisées" value={hoursDoneLabel} />}
          </div>
        </DetailsCard>
      )}
    </>
  );
};
